import { useEffect } from 'react'
import confetti from 'canvas-confetti'

// Predefined confetti animations for different achievement types
export const confettiPresets = {
  // Basic celebration - used for module/lab completion
  basic: () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    })
  },

  // Fireworks - used for major milestones (50%, 75% completion)
  fireworks: () => {
    const duration = 3000
    const animationEnd = Date.now() + duration
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 }

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min
    }

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now()

      if (timeLeft <= 0) {
        return clearInterval(interval)
      }

      const particleCount = 50 * (timeLeft / duration)
      
      // Fire from two different positions
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      })
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      })
    }, 250)
  },

  // Realistic - used for course completion
  realistic: () => {
    const count = 200
    const defaults = {
      origin: { y: 0.7 },
      zIndex: 9999
    }

    function fire(particleRatio, opts) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio)
      })
    }

    fire(0.25, {
      spread: 26,
      startVelocity: 55,
    })
    fire(0.2, {
      spread: 60,
    })
    fire(0.35, {
      spread: 100,
      decay: 0.91,
      scalar: 0.8
    })
    fire(0.1, {
      spread: 120,
      startVelocity: 25,
      decay: 0.92,
      scalar: 1.2
    })
    fire(0.1, {
      spread: 120,
      startVelocity: 45,
    })
  },

  // Stars - used for badge unlocks
  stars: () => {
    const defaults = {
      spread: 360,
      ticks: 50,
      gravity: 0,
      decay: 0.94,
      startVelocity: 30,
      shapes: ['star'],
      colors: ['FFE400', 'FFBD00', 'E89400', 'FFCA6C', 'FDFFB8'],
      zIndex: 9999
    }

    function shoot() {
      confetti({
        ...defaults,
        particleCount: 40,
        scalar: 1.2,
        shapes: ['star']
      })

      confetti({
        ...defaults,
        particleCount: 10,
        scalar: 0.75,
        shapes: ['circle']
      })
    }

    setTimeout(shoot, 0)
    setTimeout(shoot, 100)
    setTimeout(shoot, 200)
  },

  // Emoji celebration - customizable
  emoji: (emoji = '🎉') => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      scalar: 2,
      shapes: ['circle'],
      colors: ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff'],
      zIndex: 9999
    })
  },

  // Side cannons - for capstone completion
  cannons: () => {
    const end = Date.now() + 2000

    const colors = ['#2563eb', '#7c3aed', '#db2777', '#dc2626', '#ea580c']

    const frame = () => {
      confetti({
        particleCount: 2,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: colors,
        zIndex: 9999
      })
      confetti({
        particleCount: 2,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: colors,
        zIndex: 9999
      })

      if (Date.now() < end) {
        requestAnimationFrame(frame)
      }
    }

    frame()
  }
}

// Component that triggers confetti on mount
export default function ConfettiEffect({ type = 'basic', autoTrigger = true }) {
  useEffect(() => {
    if (autoTrigger && confettiPresets[type]) {
      const timer = setTimeout(() => {
        confettiPresets[type]()
      }, 100)
      
      return () => clearTimeout(timer)
    }
  }, [type, autoTrigger])

  return null // This is a non-visual component
}

// Utility function to trigger confetti manually
export const triggerConfetti = (type = 'basic') => {
  if (confettiPresets[type]) {
    confettiPresets[type]()
  }
}
